'use client'

import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import type { ImageData } from '@media-components'
import {
  FormComponent,
  type ActionButtonData,
  type SectionWrapperProps,
} from '@components/common'
import type { StyleClassNames } from '@components/common/WrapperHeader/styles'
import { CustomStyles, getStyles } from '@utils/styles'
import {
  AdvertisementBanner,
  ADVERTISEMENT_BANNER_VARIANT,
  type AdvertisementBannerProps,
} from '../AdvertisementBanner'
import defaultStyles, { StylesClassNames } from './styles'

export enum NEWSLETTER_SIZE {
  Large = 'large',
  Small = 'small',
}

export type NewsLetterData = {
  featureName?: string
  heading?: string
  description?: string
  imageUrl?: ImageData
  iconUrl?: ImageData
  actionButtonData?: ActionButtonData
}

export type NewsLetterProps = NewsLetterData & {
  csx?: CustomStyles<StylesClassNames>
  wrapperHeaderCsx?: CustomStyles<StyleClassNames>
  placeholderText?: string
  buttonText?: string
  showTextField?: boolean
  sectionWrapperProps?: Partial<SectionWrapperProps>
  newsLetterPageCategory?: AdvertisementBannerProps['newsLetterPageCategory']
  showDivider?: AdvertisementBannerProps['showDivider']
  alternateSectionBg?: boolean
  /** large = AdvertisementBanner (full section); small = compact sidebar card */
  size?: NEWSLETTER_SIZE | 'large' | 'small'
}

export function NewsLetter({
  description,
  heading,
  showTextField = true,
  sectionWrapperProps,
  newsLetterPageCategory = 'WEBSITE',
  showDivider,
  alternateSectionBg,
  size = NEWSLETTER_SIZE.Large,
  csx,
}: NewsLetterProps) {
  const tFeature = useTranslations('NewsLetterSection')
  const styles = getStyles(defaultStyles, csx)

  const resolvedHeading = heading || tFeature('heading')
  const resolvedDescription = description || tFeature('description')

  if (size === NEWSLETTER_SIZE.Small) {
    return (
      <Box {...styles('smallCard')}>
        <Typography variant="h6.600" {...styles('smallHeading')} component="h3">
          {resolvedHeading}
        </Typography>
        {resolvedDescription ? (
          <Typography variant="body3.400" {...styles('smallDescription')}>
            {resolvedDescription}
          </Typography>
        ) : null}
        {showTextField ? (
          <Box {...styles('smallForm')}>
            <FormComponent
              size="small"
              darkBg
              showIcon
              newsletterType={newsLetterPageCategory}
            />
          </Box>
        ) : null}
      </Box>
    )
  }

  return (
    <AdvertisementBanner
      variant={ADVERTISEMENT_BANNER_VARIANT.CardOnly}
      darkBg
      heading={resolvedHeading}
      description={resolvedDescription}
      showSubscribeForm={showTextField}
      newsLetterPageCategory={newsLetterPageCategory}
      noWrapper={sectionWrapperProps?.noWrapper}
      showDivider={showDivider}
      alternateSectionBg={alternateSectionBg}
    />
  )
}
